import React, { useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from './ui/button';
import { MODAL_OVERLAY_Z50_CLASS, NESTED_MODAL_CONTENT_CLASS } from './ui/modalOverlay';
import { X, Printer, Download, Save, Camera } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const StudentCardModal = ({ isOpen, onClose, student }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [cardView, setCardView] = useState('front'); // 'front' ou 'back'
  const cardRef = useRef(null);

  // Dimensions d'une carte au format standard RFID (84mm x 54mm)
  const cardWidth = 84;
  const cardHeight = 54;

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Carte Étudiante - ${student.nom} ${student.prenom}</title>
          <style>
            @page {
              size: ${cardWidth}mm ${cardHeight}mm;
              margin: 0;
            }
            body {
              margin: 0;
              padding: 0;
            }
            .card-container {
              width: ${cardWidth}mm;
              height: ${cardHeight}mm;
              overflow: hidden;
            }
          </style>
        </head>
        <body>
          <div class="card-container">
            ${cardRef.current.innerHTML}
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    // Attendre que le contenu soit chargé puis imprimer
    printWindow.onload = function() {
      printWindow.print();
    };
  };

  const handleDownloadPDF = async () => {
    setIsGenerating(true);
    
    try {
      // Utiliser html2canvas pour convertir le HTML en image
      const canvas = await html2canvas(cardRef.current, {
        scale: 3, // Une échelle plus élevée pour une meilleure qualité
        useCORS: true,
        allowTaint: true
      });
      
      // Convertir en PDF avec les bonnes dimensions
      const imgData = canvas.toDataURL('image/png');
      
      // Créer un PDF au format de la carte
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [cardWidth, cardHeight]
      });
      
      pdf.addImage(imgData, 'PNG', 0, 0, cardWidth, cardHeight);
      pdf.save(`carte_etudiant_${student.matricule}.pdf`);
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Pour télécharger en tant qu'image PNG
  const handleDownloadImage = async () => {
    setIsGenerating(true);
    
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 3,
        useCORS: true,
        allowTaint: true
      });
      
      const link = document.createElement('a');
      link.download = `carte_etudiant_${student.matricule}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Erreur lors de la génération de l\'image:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  if (!student) return null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        {/* La carte s'ouvre depuis le dossier de l'élève, sur les calques hauts
            (modalOverlay.js) : elle prend le calque des fenêtres imbriquées,
            sinon elle s'ouvrait dessous (04/11/2026). */}
        <Dialog.Overlay className={MODAL_OVERLAY_Z50_CLASS} />
        <Dialog.Content className={`fixed top-[50%] left-[50%] transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto ${NESTED_MODAL_CONTENT_CLASS}`}>
          <div className="sticky top-0 bg-white z-10 rounded-t-xl">
            <div className="p-5 border-b border-gray-200 flex justify-between items-center">
              <Dialog.Title className="text-xl font-bold text-gray-800">
                Carte Étudiante
              </Dialog.Title>
              <Dialog.Close asChild>
                <button className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 focus:outline-none transition-colors">
                  <X size={20} className="text-gray-700" />
                </button>
              </Dialog.Close>
            </div>
          </div>

          <div className="p-6">
            <div className="flex flex-col items-center gap-8">
              {/* Options d'affichage (recto/verso) */}
              <div className="flex gap-4 bg-gray-100 p-1 rounded-full self-center">
                <button 
                  className={`px-4 py-2 rounded-full ${cardView === 'front' ? 'bg-white shadow' : 'text-gray-600'}`}
                  onClick={() => setCardView('front')}
                >
                  Recto
                </button>
                <button 
                  className={`px-4 py-2 rounded-full ${cardView === 'back' ? 'bg-white shadow' : 'text-gray-600'}`}
                  onClick={() => setCardView('back')}
                >
                  Verso
                </button>
              </div>



              {/* Prévisualisation de la carte - selon le design existant dans DetailModal */}
              <div className="border border-gray-300 rounded-lg shadow-md bg-white overflow-hidden" style={{maxWidth: '100%'}}>
                <div ref={cardRef} className="bg-white">
                  {cardView === 'front' ? (
                    /* RECTO DE LA CARTE - Utilise exactement le design actuel */
                    <div className="p-4 flex flex-col items-center">
                      <div className="w-full bg-[#246990] text-white p-3 rounded-t-lg flex justify-between items-center">
                        <div className="font-bold">CARTE ÉTUDIANTE</div>
                        <div className="text-sm">Année Scolaire 2024-2025</div>
                      </div>
                      
                      <div className="flex w-full p-4 gap-4">
                        <div className="w-1/3">
                          {/* L'ADRESSE DE LA PHOTO EST LE CHEMIN RELATIF RENDU PAR L'API.

                              POURQUOI l'adresse absolue `https://www.klasys.com${student.photo_url}`
                              etait fausse : elle demandait la photo au domaine de www QUELLE QUE
                              SOIT l'ecole de l'utilisateur. Depuis csap1, la carte d'eleve allait
                              donc chercher le fichier chez www -- et cela ne << marchait >> que
                              parce que uploads/photos etait un dossier COMMUN a toutes les ecoles,
                              servi en statique et sans jeton.

                              Les photos sont desormais rangees par ecole
                              (uploads/photos/<sous-domaine>/) et l'adresse rendue par l'API est
                              signee pour l'ecole de la requete : une adresse signee pour csap1 ne
                              vaut rien sur le domaine de www. Le chemin relatif, lui, part sur le
                              domaine de l'ecole courante et emporte sa signature telle quelle.
                              C'est ce que font deja les cinq autres ecrans qui affichent une photo
                              (OrganigrammeRH, PortailEnseignant, VieScolairePage, MessageForm,
                              ParametresOrganigramme) : on ne batit plus d'adresse ici.

                              Effet de bord utile : la photo devient de MEME ORIGINE que la page.
                              html2canvas ne tache donc plus le canvas, et la photo apparait enfin
                              dans le PDF et le PNG -- `useCORS` et `allowTaint` ne pouvaient rien
                              pour une image servie par un autre domaine sans en-tete CORS. */}
                          {student.photo_url ? (
                            <img 
                              src={student.photo_url}
                              alt="Photo d'identité" 
                              className="w-full h-auto object-cover rounded-lg border-2 border-gray-200"
                            />
                          ) : (
                            <div className="w-full h-40 flex flex-col items-center justify-center bg-gray-100 rounded-lg border-2 border-gray-200">
                              <Camera size={40} className="text-gray-300 mb-2" />
                              <span className="text-gray-500 text-sm text-center">Aucune photo</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="w-2/3 space-y-2">
                          <div>
                            <div className="text-xs text-gray-500">Nom et prénom</div>
                            <div className="font-semibold">{student.nom} {student.prenom}</div>
                          </div>
                          
                          <div>
                            <div className="text-xs text-gray-500">Matricule</div>
                            <div className="font-semibold">{student.matricule || "Non défini"}</div>
                          </div>
                          
                          <div>
                            <div className="text-xs text-gray-500">Classe</div>
                            <div className="font-semibold">{student.classe_nom}</div>
                          </div>
                          
                          <div>
                            <div className="text-xs text-gray-500">Date de naissance</div>
                            <div className="font-semibold">
                              {student.dateNaissance || student.date_naissance ? 
                                new Date(student.dateNaissance || student.date_naissance).toLocaleDateString() : 
                                'Non renseigné'}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="w-full bg-gray-100 p-3 text-center text-xs text-gray-600 rounded-b-lg">
                        Cette carte est personnelle et doit être présentée à l'entrée de l'établissement
                      </div>
                    </div>
                  ) : (
                    /* VERSO DE LA CARTE */
                    <div className="p-4 flex flex-col items-center">
                      <div className="w-full bg-[#246990] text-white p-3 rounded-t-lg flex justify-between items-center">
                      
                        <div className="font-bold">INFORMATIONS</div>
                      </div>
                      
                      <div className="flex w-full p-4 gap-4">
                        
                        
                      <div className="w-full bg-gray-100 p-3 text-center text-xs text-gray-600 rounded-b-lg">
                        <p>La présente carte est la propriété exclusive de l'établissement.</p>
                        <p>Elle doit être présentée à toute demande du personnel autorisé.</p>
                        <p>En cas de perte, merci d'en informer immédiatement l'administration.</p>
                        </div>

                        
                        <div className="w-full bg-gray-100 p-3 text-center text-xs text-gray-600 rounded-b-lg">
                        <div className="mt-3 p-3 border border-dashed border-gray-300 rounded">
                          <p className="font-bold">Contact en cas d'urgence:</p>
                          <p>{student.parent?.nom_prenom || 'Non renseigné'}</p>
                          <p>Tél: {student.parent?.telephone || 'Non renseigné'}</p>
                        </div>
                      </div>
                      </div>
                      
                      <div className="w-full bg-gray-100 p-3 text-center text-xs text-gray-600 rounded-b-lg">
                        <div className="mb-1">Adresse de l'établissement</div>
                        <div className="font-medium">123 Rue de l'Éducation, 75000 Paris</div>
                        <div>contact@ecole-exemple.com · +33 1 23 45 67 89</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>


              

              {/* Actions */}
              <div className="flex gap-4 w-full justify-center">
                <Button
                  onClick={handlePrint}
                  className="flex items-center gap-2 bg-[#c66d59] hover:bg-[#ae7467]/90 text-white text-base px-5 py-2 h-auto rounded-lg"
                  disabled={isGenerating}
                >
                  <Printer size={18} />
                  Imprimer
                </Button>
                <Button
                  onClick={handleDownloadPDF}
                  className="flex items-center gap-2 bg-[#246990] hover:bg-[#246990]/90 text-white text-base px-5 py-2 h-auto rounded-lg"
                  disabled={isGenerating}
                >
                  {isGenerating ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-l-2 border-white"></div>
                  ) : (
                    <Save size={18} />
                  )}
                  Télécharger PDF
                </Button>
                <Button
                  onClick={handleDownloadImage}
                  className="flex items-center gap-2 bg-[#319190] hover:bg-[#63b5b4]/90 text-white text-base px-5 py-2 h-auto rounded-lg"
                  disabled={isGenerating}
                >
                  <Download size={18} />
                  Télécharger PNG
                </Button>
              </div>
              
              <div className="text-sm text-gray-500 text-center max-w-md">
                <p>La carte sera générée au format standard de badge RFID (84mm × 54mm).</p>
                <p className="mt-1">Utilisez du papier épais ou une imprimante de cartes spécialisée pour de meilleurs résultats.</p>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default StudentCardModal;